import { FormProvider, useForm } from 'react-hook-form'
import { CompanyDetail } from 'components/molecules/CompanyDetail'
import { SalesPersons } from 'components/molecules/SalesPersons'
import { Memo } from 'components/atoms/inputs/Memo'
import swal from 'sweetalert2'
import { MainPage } from 'components/templates/MainPage'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { insertCompany } from 'api/company/insertCompany'
import {
  CompanyCreateSchema,
  CompanyCreateSchemaType
} from 'schema/company/CompanyCreateSchema'
import { removeUndefinedProperties } from 'utils/removeUndefinedProperties'

export const CompanyCreate = (): JSX.Element | null => {
  const navigate = useNavigate()
  const methods = useForm<CompanyCreateSchemaType>({
    defaultValues: {
      frequencyCode: 'NONE',
      tagCode: 'NONE',
      dispatchLicense: null,
      pMark: null,
      isms: null,
      focusCustomer: false,
      sales: [{}]
    },
    resolver: zodResolver(CompanyCreateSchema)
  })
  const onSubmit = async (data: CompanyCreateSchemaType) => {
    const convert = removeUndefinedProperties(data)
    const convertSales = data.sales.map((salesUser) => {
      const splitName = salesUser.fullName.split(' ')
      return {
        lastName: splitName[0],
        firstName: splitName[1],
        mail: salesUser.mail,
        department: salesUser.department || undefined,
        cellPhone: salesUser.cellPhone || undefined,
        post: salesUser.post || undefined
      }
    })
    await insertCompany({
      ...convert,
      name: data.name,
      frequencyCode:
        data.frequencyCode === 'NONE' ? undefined : data.frequencyCode,
      tagCode: data.tagCode === 'NONE' ? undefined : data.tagCode,
      focusCustomer: data.focusCustomer,
      sales: convertSales
    })
      .then((response) => {
        swal
          .fire({
            icon: 'info',
            text: '会社情報を登録しました。',
            confirmButtonText: 'OK'
          })
          .then((result) => {
            if (result.isConfirmed) {
              navigate(`/company`)
            } else {
              return
            }
          })
      })
      .catch((error: any) => {
        swal.fire({
          icon: 'error',
          text: error?.data?.message || '会社情報の登録に失敗しました。',
          confirmButtonText: 'OK'
        })
      })
  }

  return (
    <MainPage>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div
            className={
              'rounded-lg bg-white p-6 shadow-[0_2px_4px_rgba(0,0,0,0.08)]'
            }
          >
            <div className={'grid grid-cols-1 gap-6 lg:grid-cols-[1fr_400px]'}>
              <div>
                <CompanyDetail />
                <SalesPersons />
                <div className={'mt-8 text-center'}>
                  <button
                    type={'submit'}
                    className={
                      'rounded-md bg-[#01695C] px-12 py-3 text-base font-medium text-white transition-colors hover:bg-[#015a4e]'
                    }
                  >
                    上記の内容で新規登録
                  </button>
                </div>
              </div>
              <div
                className={
                  'sticky top-[100px] h-fit rounded-lg bg-[#f8f9fa] p-5'
                }
              >
                <div className={'mb-3 text-sm font-semibold text-[#666]'}>
                  メモ
                </div>
                <Memo
                  register={methods.register('memo')}
                  placeHolder={'先方担当者：\n注力情報：\n業界：'}
                />
                <div className={'mt-2 text-sm text-[#999]'}>
                  訪問履歴等のメモを記録できます
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </MainPage>
  )
}
